import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { XIcon } from 'lucide-react';
import { Box, FileInput, Spacing, Text } from '../../ui';
import { LayoutBox, TextData } from '../../components';
import { Grid } from '../../ui/components/Grid';
import { useApi, useAsync } from '../../hooks';
import { buildLossColumns, lossRowClassRules } from './lossColumns';
import { LossCorrectionDiffDialog } from './LossCorrectionDiffDialog';
import { ReplaceLossHistoryDialogs } from './ReplaceLossHistoryDialogs';
import { lossCsvTemplate, parseLossCsv } from './lossCsvImport';
import { validateStaging } from './lossValidation';
import { saveErrorText, saveLossChanges } from './saveLossChanges';
import {
  appendImportedRows,
  appendNewRow,
  cloneRowsForEdit,
  decorateRows,
  diffRows,
  isDirty,
  isSkyward,
  restageFailures,
  snapshotRows,
  toggleRowRemoval,
  toggleRowRestore,
  toggleRowRevert,
  withRowKeys,
} from './lossStaging';
import { Alert, AlertAction, AlertDescription } from '../../components/ui/alert';
import { Button as ShadButton } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';

/* The count and warnings accumulate over an edit session's imports rather than being replaced: the
   values each warning reports as dropped or substituted are still sitting in the grid, so a later
   import must not take the only thing that says so off the screen. An error replaces only itself,
   because whatever landed before it is still staged. */
const EMPTY_IMPORT_SUMMARY = { count: 0, warnings: [], error: null };

// The three the Total Incurred formula reads.
const MONEY_COLUMNS = ['incurred', 'expense', 'recovery'];

/* Doubles as the banner and as the reason Save is closed, so it names the way out. There is no retry
   button to offer: the rows the grid holds are the ones it could not replace. */
const STALE_LIST_MESSAGE = 'The loss list could not be reloaded, so what is shown may be out of date. Reload the page before saving.';

/* Built here rather than fetched: the template is the importer's own header list, so it needs no
   losses, no application, and no round trip. */
const saveLossCsvTemplate = () => {
  const url = window.URL.createObjectURL(new Blob([lossCsvTemplate()], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'loss-history-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const FILL_HANDLE_CELL_SELECTION = { handle: { mode: 'fill' } };

const LossManagement = memo(function LossManagement(props) {
  const { application, refreshApplicationData, refreshing = false, refreshFailed = false, editDisabled = false, editDisabledReason } = props;

  const api = useApi();

  // AG Grid owns the rows while editing: `editRows` are clones it writes cell values into, `snapshot`
  // is what "changed" is measured against, and `revision` exists because those writes happen outside
  // React — without bumping it the validation banner and the Save button would go stale.
  const [editRows, setEditRows] = useState(null);
  const [snapshot, setSnapshot] = useState({});
  const [revision, setRevision] = useState(0);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [diffRow, setDiffRow] = useState(null);
  const [saveErrors, setSaveErrors] = useState([]);
  const [downloadError, setDownloadError] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [restagePending, setRestagePending] = useState(null);
  // Clear all / Adopt from… (UND-987) live in ReplaceLossHistoryDialogs — this is only what
  // LossManagement itself needs: `replaceBusy` folds into its own `busy` (Add Loss, Save, Edit
  // Losses… stay disabled for the moment a replace PUT is actually in flight), and `replaceNotice`
  // is the dismissible, non-persisted banner shown after one lands (see `handleReplaced`).
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [replaceNotice, setReplaceNotice] = useState(null);
  const gridApi = useRef(null);
  const editing = editRows !== null;

  const handleGetLossCauses = useCallback(async () => api.getLossCauses(application.product.id), [api, application.product.id]);
  const { value: lossCauses, error: loadLossCausesError } = useAsync(handleGetLossCauses);

  const serverRows = useMemo(() => (Array.isArray(application.lossHistory) ? application.lossHistory : []), [application]);
  const locations = useMemo(() => (Array.isArray(application.locations) ? application.locations : []), [application]);
  // A self-reported row shadowed by a Skyward twin comes back flagged `suppressed` so a save never
  // deletes it, but it stays out of the grid entirely — it is never staged, never shown, never edited.
  const suppressedRows = useMemo(() => serverRows.filter((row) => row.suppressed), [serverRows]);
  const visibleServerRows = useMemo(() => serverRows.filter((row) => !row.suppressed), [serverRows]);
  const lockedRows = useMemo(() => withRowKeys(visibleServerRows), [visibleServerRows]);
  const rows = editing ? editRows : lockedRows;

  // revision is a dependency because AG Grid mutates the row objects in place, leaving the array identity
  // unchanged; without it the diff would never see an edit. Locked mode has no snapshot to diff
  // against, and diffing against an empty one would mark every field as edited and hand validation
  // rows it should never judge.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const edits = useMemo(() => (editing ? diffRows(rows, snapshot) : {}), [editing, rows, snapshot, revision]);
  const displayRows = useMemo(() => decorateRows(rows, edits), [rows, edits]);
  const { valid, cellErrors, messages } = useMemo(() => validateStaging(displayRows), [displayRows]);
  const visibleCount = useMemo(() => displayRows.filter((row) => !row._removed && !row.excluded).length, [displayRows]);

  // The error map is a fresh object per validation pass, so the column defs read it through a ref:
  // handing AG Grid a new columnDefs identity makes it reload its columns, and that clears the undo
  // stack the cell edit was just pushed onto.
  const cellErrorsRef = useRef(cellErrors);
  cellErrorsRef.current = cellErrors;

  // The ref lands during render, which can be after AG Grid has already refreshed the edited cell, so
  // a changed error set has to re-run the class rules itself.
  const errorSignature = useMemo(() => JSON.stringify(cellErrors), [cellErrors]);
  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.refreshCells({ force: true });
    }
  }, [errorSignature]);

  // Total Incurred is derived from the money columns, and AG Grid caches a valueGetter's result,
  // so editing one of them has to explicitly invalidate that cell — the rowData array is unchanged.
  // Flags is refreshed with it, so typing an indemnity clears Needs indemnity.
  const onCellValueChanged = useCallback((params) => {
    setRevision((r) => r + 1);
    const api = gridApi.current;
    if (!api || !params || !params.node) {
      return;
    }
    /* Flagged on the row because the column def is what reads it and cannot see the staging diff. It
       is what tells an untouched Skyward total apart from one whose components the user has started
       changing — the first keeps the feed's figure, the second previews ours. */
    if (params.column && MONEY_COLUMNS.includes(params.column.getColId())) {
      params.data._moneyEdited = true;
    }
    api.refreshCells({ rowNodes: [params.node], columns: ['totalLossAmount', 'flags'], force: true });
  }, []);

  // Flag toggles mutate the row, so the grid has to be told to re-evaluate that row's classes and
  // its action cell — nothing about the rowData array changed.
  const refreshRow = useCallback((row) => {
    setRevision((r) => r + 1);
    const api = gridApi.current;
    const node = api && api.getRowNode(row._key);
    if (node) {
      api.redrawRows({ rowNodes: [node] });
    }
  }, []);

  const onToggleRemoval = useCallback(
    (row) => {
      if (row._new) {
        // A row added this session exists nowhere but here, so removing it just leaves the edit set.
        // Flagging it instead would stage a delete against a row with no id.
        setEditRows((current) => current.filter((staged) => staged._key !== row._key));
        setRevision((r) => r + 1);
        return;
      }
      toggleRowRemoval(row);
      refreshRow(row);
    },
    [refreshRow]
  );

  const onToggleRevert = useCallback(
    (row) => {
      toggleRowRevert(row);
      refreshRow(row);
    },
    [refreshRow]
  );

  const onToggleRestore = useCallback(
    (row) => {
      toggleRowRestore(row);
      refreshRow(row);
    },
    [refreshRow]
  );

  const onAddRow = useCallback(() => setEditRows((current) => appendNewRow(current, uuid())), []);

  const onImportCsv = useCallback(
    (files) => {
      const file = files && files[0];
      if (!file) {
        return;
      }
      const reportFailure = (message) => setImportSummary((current) => ({ ...(current || EMPTY_IMPORT_SUMMARY), error: message }));
      const staged = editRows || [];
      const reader = new FileReader();
      reader.onerror = () => reportFailure(`⚠ Could not read ${file.name}`);
      reader.onload = (event) => {
        const {
          rows: imported,
          warnings,
          error,
        } = parseLossCsv(String(event.target.result), {
          lossCauses,
          locations,
          // Diffed against what is staged, not the server rows, so a second import sees the first.
          existingClaimNumbers: staged.filter((row) => !isSkyward(row)).map((row) => row.claimNumber),
          skywardClaimNumbers: staged.filter(isSkyward).map((row) => row.claimNumber),
        });
        if (error) {
          reportFailure(error);
          return;
        }
        setEditRows((current) => appendImportedRows(current, imported, uuid));
        setRevision((r) => r + 1);
        setImportSummary((current) => {
          const base = current || EMPTY_IMPORT_SUMMARY;
          return { count: base.count + imported.length, warnings: [...base.warnings, ...warnings], error: null };
        });
      };
      reader.readAsText(file);
    },
    [lossCauses, locations, editRows]
  );

  const onFlagClicked = useCallback((row) => setDiffRow(row), []);

  const onDownloadCsv = useCallback(async () => {
    setDownloadError(null);
    try {
      await api.downloadLossHistoryCSV(application.id);
    } catch (error) {
      setDownloadError(saveErrorText(error, 'Could not download the loss CSV'));
    }
  }, [api, application.id]);

  const columns = useMemo(
    () => buildLossColumns({ editing, lossCauses, locations, cellErrorsRef, onToggleRemoval, onToggleRevert, onToggleRestore, onFlagClicked }),
    [editing, lossCauses, locations, onToggleRemoval, onToggleRevert, onToggleRestore, onFlagClicked]
  );

  const getRowId = useCallback((params) => params.data._key, []);
  const gridOptions = useMemo(() => ({ domLayout: 'autoHeight', rowHeight: 32 }), []);
  const setGridReference = useCallback((params) => {
    gridApi.current = params.api;
  }, []);

  const enterEditing = useCallback(() => {
    const clones = cloneRowsForEdit(visibleServerRows);
    setEditRows(clones);
    setSnapshot(snapshotRows(clones));
    setRevision(0);
    setImportSummary(null);
  }, [visibleServerRows]);

  // Discard needs no undo: only the clones were ever mutated, so dropping them restores the feed.
  const exitEditing = useCallback(() => {
    setEditRows(null);
    setSnapshot({});
    setRevision(0);
    setSaveErrors([]);
    setImportSummary(null);
    setConfirmDiscard(false);
  }, []);

  const onPressDiscard = useCallback(() => {
    if (isDirty(rows, snapshot)) {
      setConfirmDiscard(true);
    } else {
      exitEditing();
    }
  }, [rows, snapshot, exitEditing]);

  const onPressSave = useCallback(async () => {
    if (!valid || saving) {
      return;
    }
    setSaving(true);
    try {
      // Re-diffed here rather than taken from the render-time memos: AG Grid writes cell values into
      // the row objects outside React, so what to send is only knowable at the moment of the click.
      const currentRows = editRows || [];
      const currentEdits = diffRows(currentRows, snapshot);
      const currentDisplayRows = decorateRows(currentRows, currentEdits);

      const { failures } = await saveLossChanges({
        applicationId: application.id,
        displayRows: currentDisplayRows,
        suppressedRows,
        staging: { edits: currentEdits },
        api,
      });

      try {
        await refreshApplicationData(true);
      } catch (error) {
        /* Swallowed so it cannot abort the rest of this handler: the writes have already happened, and
           which of them failed is reported nowhere but below. That the list may now be stale is a
           separate signal — the host reports it through `refreshFailed`, since a refresher built on
           useAsync resolves either way and keeps its failure in its own state. */
      }

      if (failures.length) {
        // Everything that succeeded is committed. Hand the failed rows to an effect so they are
        // re-applied onto the refreshed server rows — retrying must not re-send a row that landed,
        // which for a new row would create it twice.
        const failedKeys = failures.map((f) => f.key);
        setRestagePending(currentDisplayRows.filter((row) => failedKeys.includes(row._key)));
        setSaveErrors(failures.map((f) => `${f.label}: ${saveErrorText(f.error)}`));
      } else {
        exitEditing();
      }
    } finally {
      setSaving(false);
    }
  }, [valid, saving, application.id, editRows, snapshot, suppressedRows, api, refreshApplicationData, exitEditing]);

  useEffect(() => {
    if (!restagePending) {
      return;
    }
    // Snapshot the refreshed rows before re-applying the failures, so the retry diff contains only
    // what still needs saving — the rows that landed are now the baseline.
    // This reads `application.lossHistory` (via `visibleServerRows`) already refreshed because React 17's legacy render commits the refresh's
    // setState synchronously, outside event batching. A move to React 18 createRoot would defer that
    // commit and hand this effect the pre-refresh rows, which would drop what did save out of the grid
    // entirely: snapshot and rows come from the same clones, so a committed edit reads as no edit.
    const fresh = cloneRowsForEdit(visibleServerRows);
    setSnapshot(snapshotRows(fresh));
    setEditRows(restageFailures(fresh, restagePending));
    setRevision((r) => r + 1);
    setRestagePending(null);
  }, [restagePending, visibleServerRows]);

  const busy = saving || refreshing || replaceBusy;

  // Per-row edits staged this session that Confirm would silently throw away — the modal has to say
  // so before it does.
  const hasUnsavedEdits = editing && isDirty(rows, snapshot);

  // ReplaceLossHistoryDialogs calls this once its PUT has landed; the sequence — refresh, then drop
  // out of the per-row staging session, then show the notice — is the same one onPressSave runs
  // after a successful save.
  const handleReplaced = useCallback(
    async (notice) => {
      try {
        await refreshApplicationData(true);
      } catch (error) {
        /* Swallowed for the same reason onPressSave swallows it: the write already landed, and the
           host's own `refreshFailed` state is what reports the list may now be stale. */
      }
      exitEditing();
      setReplaceNotice(notice);
    },
    [refreshApplicationData, exitEditing]
  );

  return (
    <LayoutBox layout={!displayRows.length ? 'center' : 'top-left'} stretch minHeight="400">
      <Spacing vertical="5" />
      {loadLossCausesError ? (
        <Text color="$error" bold small>
          Error loading available Loss Causes. Editing a loss cause may not work.
        </Text>
      ) : null}
      <LayoutBox row layout="center" justifyContent="space-between" width="100%" my="$1" px="$2">
        <Box flexDirection="row" alignItems="center">
          {editing ? (
            <>
              <ShadButton size="sm" onClick={onAddRow} disabled={busy}>
                Add Loss
              </ShadButton>
              <Spacing horizontal="1" />
              {/* inputFieldIcon renders a caller-supplied control under FileInput's transparent
                  file label, so this reads as the same button as Add Loss beside it. */}
              <FileInput
                accept=".csv,text/csv"
                disabled={busy}
                onChangeValue={onImportCsv}
                inputFieldIcon={
                  <ShadButton size="sm" disabled={busy}>
                    Import CSV
                  </ShadButton>
                }
              />
            </>
          ) : null}
          {editing ? <Spacing horizontal="1" /> : null}
          <ShadButton size="sm" onClick={onDownloadCsv} disabled={busy || !displayRows.length}>
            Download Loss CSV
          </ShadButton>
          <Spacing horizontal="1" />
          <ShadButton size="sm" onClick={saveLossCsvTemplate}>
            Download Template
          </ShadButton>
          <ReplaceLossHistoryDialogs
            application={application}
            hasUnsavedEdits={hasUnsavedEdits}
            disabled={busy || editDisabled}
            onBusyChange={setReplaceBusy}
            onReplaced={handleReplaced}
          />
          {!editing && editDisabled && editDisabledReason ? (
            <Text size="small" color="$gray.500">
              {editDisabledReason}
            </Text>
          ) : null}
        </Box>
        <Box flexDirection="row" alignItems="center">
          {editing ? (
            <>
              <ShadButton
                variant="outline"
                size="sm"
                className="border-primary text-primary hover:bg-primary/10 hover:text-primary"
                onClick={onPressDiscard}
                disabled={saving}
              >
                Discard
              </ShadButton>
              {/* `gap` is mapped in the style scales but never declared as a style prop, so it is
                  silently dropped — Spacing is how this design system separates buttons. */}
              <Spacing horizontal="1" />
              <ShadButton size="sm" onClick={onPressSave} disabled={busy || !valid || refreshFailed}>
                {saving ? 'Saving…' : 'Save'}
              </ShadButton>
            </>
          ) : (
            <ShadButton size="sm" onClick={enterEditing} disabled={busy || editDisabled}>
              Edit Losses
            </ShadButton>
          )}
        </Box>
      </LayoutBox>
      {/* Not persisted: a page reload, or another replace, drops it — it names what just happened,
          not a standing fact about the grid. */}
      {replaceNotice ? (
        <Box mt="$1">
          <Alert>
            <AlertDescription>{replaceNotice}</AlertDescription>
            <AlertAction>
              <ShadButton variant="ghost" size="icon-sm" onClick={() => setReplaceNotice(null)} aria-label="Dismiss">
                <XIcon />
              </ShadButton>
            </AlertAction>
          </Alert>
        </Box>
      ) : null}
      {messages.length ? (
        <Box mt="$1">
          {/* Keyed by position because two rows can fail the same rule and produce identical text. */}
          {messages.map((message, index) => (
            <Text key={index} color="$error" small>
              {message}
            </Text>
          ))}
        </Box>
      ) : null}
      {saveErrors.length ? (
        <Box mt="$1">
          <Text color="$error" bold small>
            Some losses did not save. They are still unsaved below.
          </Text>
          {saveErrors.map((message, index) => (
            <Text key={index} color="$error" small>
              {message}
            </Text>
          ))}
        </Box>
      ) : null}
      {/* Same treatment as the save failures, because it is the same kind of problem: rows that cannot be
          trusted to say what is committed. It doubles as the reason Save is disabled — a staged row that
          already landed is no longer among these rows, so saving again would create it a second time. */}
      {refreshFailed ? (
        <Box mt="$1">
          <Text color="$error" bold small>
            {STALE_LIST_MESSAGE}
          </Text>
        </Box>
      ) : null}
      {downloadError ? (
        <Box mt="$1">
          <Text color="$error" bold small>
            {downloadError}
          </Text>
        </Box>
      ) : null}
      {importSummary ? (
        <Box mt="$1">
          {/* A failed import and a successful earlier one are not alternatives: the rows the count
              covers are still staged, so both lines can be true at once. */}
          {importSummary.error ? (
            <Text color="$error" bold small>
              {importSummary.error}
            </Text>
          ) : null}
          {importSummary.count ? <Text small>{`Imported ${importSummary.count} ${importSummary.count === 1 ? 'loss' : 'losses'}.`}</Text> : null}
          {/* Keyed by position because this is an append-only log across the session's imports, so two
              entries can legitimately read identically. Each names data that was dropped or
              substituted, so they take the same treatment as the validation banner rather than a
              quieter one. */}
          {importSummary.warnings.map((message, index) => (
            <Text key={index} color="$error" small>
              {message}
            </Text>
          ))}
        </Box>
      ) : null}
      <Spacing vertical={3} />
      {displayRows.length ? (
        <Box width="100%">
          <Grid
            data={rows}
            columns={columns}
            setGridReference={setGridReference}
            floatingFilter={false}
            pageSize={100}
            gridOptions={gridOptions}
            getRowId={getRowId}
            rowClassRules={lossRowClassRules}
            cellSelection={editing ? FILL_HANDLE_CELL_SELECTION : true}
            stopEditingWhenCellsLoseFocus
            onCellValueChanged={onCellValueChanged}
          />
        </Box>
      ) : (
        <Text center mb="$2">
          No records found
        </Text>
      )}
      <Box>
        <TextData data={`${visibleCount} losses`} />
      </Box>
      <Dialog open={confirmDiscard} onOpenChange={(open) => !open && setConfirmDiscard(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard changes?</DialogTitle>
            <DialogDescription>Your unsaved loss edits will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <ShadButton variant="ghost" className="text-primary hover:text-primary" onClick={() => setConfirmDiscard(false)}>
              Cancel
            </ShadButton>
            <ShadButton onClick={exitEditing}>Discard changes</ShadButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {diffRow ? <LossCorrectionDiffDialog row={diffRow} applicationId={application.id} onClose={() => setDiffRow(null)} /> : null}
    </LayoutBox>
  );
});

export { LossManagement };
